"use client";

import {
  ToastProvider as ShadcnToastProvider,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import React, { createContext, ReactNode, useContext, useState } from "react";

interface ToastContextType {
  showToast: (params: {
    title?: string;
    description?: string;
    variant?: "default" | "destructive" | "success";
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState({
    open: false,
    title: "",
    description: "",
    variant: "default" as "default" | "destructive" | "success",
    action: undefined as { label: string; onClick: () => void } | undefined,
    duration: 4000,
  });

  const showToast = ({
    title = "",
    variant = "success",
    description = variant === "success" || variant === undefined
      ? "Guardado correctamente"
      : "",
    action,
    duration = variant === "success" ? 3000 : 4000,
  }: {
    title?: string;
    description?: string;
    variant?: "default" | "destructive" | "success";
    action?: { label: string; onClick: () => void };
    duration?: number;
  }) => {
    setToast({ open: true, title, description, variant, action, duration });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const contextValue: ToastContextType = { showToast };

  return (
    <ToastContext.Provider value={contextValue}>
      <ShadcnToastProvider duration={toast.duration}>
        {children}
        <Toast
          open={toast.open}
          onOpenChange={hideToast}
          variant={toast.variant}
        >
          <div className="grid gap-1">
            {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
            {toast.description && (
              <ToastDescription>{toast.description}</ToastDescription>
            )}
          </div>
          {toast.action && (
            <ToastAction
              altText={toast.action.label}
              onClick={toast.action.onClick}
              variant={toast.variant}
            >
              {toast.action.label}
            </ToastAction>
          )}
          <ToastClose />
        </Toast>
        <ToastViewport />
      </ShadcnToastProvider>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
